/**
 * useAuth Hook - Provides easy access to authentication state and methods
 * Follows "Joyful Professional" design principles
 */

import { useAuthContext } from '../contexts/AuthContext';

/**
 * Hook to access authentication state and methods
 * @returns {Object} Auth context value
 */
export const useAuth = () => {
  return useAuthContext();
};

/**
 * Hook to check if user is authenticated
 * @returns {boolean} True if user is authenticated
 */
export const useIsAuthenticated = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
};

/**
 * Hook to get current user
 * @returns {Object|null} Current user object or null
 */
export const useCurrentUser = () => {
  const { user } = useAuth();
  return user;
};

/**
 * Hook to get active role
 * @returns {Object|null} Active role object or null
 */
export const useActiveRole = () => {
  const { user } = useAuth();
  return user?.active_role || null;
};

/**
 * Hook to check if user has multiple roles
 * @returns {boolean} True if user has multiple roles
 */
export const useHasMultipleRoles = () => {
  const { user } = useAuth();
  return user?.roles && user.roles.length > 1;
};

export default useAuth;

